import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Logo } from "@/components/Brand";
import logo from "@/assets/pi360-logo.png";
import { Video, Camera, Mic, Sparkles, FileText, ShieldCheck, Check, ArrowRight, Building2, Lock } from "lucide-react";

const VALUE = [
  { icon: Video, title: "Fast Field Capture", desc: "Record short clips and snap photos as you walk each vacant unit — one-handed." },
  { icon: Mic, title: "Voice-to-Text Notes", desc: "Dictate findings out loud. Whisper transcribes them instantly, no typing." },
  { icon: Sparkles, title: "AI Draft Reports", desc: "Turn raw notes and findings into a clean, editable executive summary in one tap." },
  { icon: FileText, title: "Bank-Ready Exports", desc: "Generate printable PDF and CSV damage & inventory reports for managers and banks." },
];

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const nav = useNavigate();

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-body text-slate-900">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <Logo className="h-12" />
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login-link" className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">Sign in</Link>
            <Link to="/register" data-testid="nav-register-link" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700">Start free</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/90 to-slate-900" />
        <div className="pointer-events-none absolute -left-28 top-8 h-80 w-80 rounded-full bg-blue-600/25 blur-3xl" />
        <div className="pointer-events-none absolute -right-10 top-1/3 h-96 w-96 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-400/40 bg-blue-500/10 px-3 py-1 font-mono text-xs uppercase tracking-widest text-blue-300">
              Inspect • Document • Manage • Anywhere
            </span>
            <h1 className="mt-6 font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
              Walk the unit.<br /><span className="bg-gradient-to-r from-blue-400 to-sky-300 bg-clip-text text-transparent">Ship the report.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
              PropertyInspect360 turns a 5-minute vacant-unit walkthrough into a clean, printable damage & inventory report your property managers and banks trust — with video, photos, dictated notes and AI-assisted summaries.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-start-btn" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-base font-semibold shadow-lg shadow-blue-900/40 transition-transform hover:-translate-y-0.5 hover:bg-blue-700">
                Start free trial <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#pricing" data-testid="hero-pricing-btn" className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-6 py-3.5 text-base font-semibold hover:bg-white/10">
                View pricing
              </a>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="relative flex justify-center lg:justify-end">
            <div className="relative">
              <div className="absolute -inset-8 rounded-full bg-gradient-to-tr from-blue-600/40 via-sky-400/20 to-transparent blur-3xl" />
              <div className="absolute -inset-4 animate-spin-slow rounded-[3rem] border border-dashed border-blue-400/30" />
              <div className="relative animate-float rounded-[2.5rem] border border-white/10 bg-white p-6 shadow-2xl shadow-blue-950/50 sm:p-8">
                <img src={logo} alt="PropertyInspect360" className="h-60 w-60 object-contain sm:h-72 sm:w-72" />
              </div>
              <div className="absolute -bottom-4 -right-2 rotate-3 rounded-xl bg-emerald-500 px-4 py-2 font-display text-sm font-bold uppercase tracking-wide text-white shadow-lg">
                Bank-Ready ✓
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value props */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-900 sm:text-3xl">Built for the field, not the desk</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {VALUE.map((v, i) => (
            <motion.div key={v.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><v.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-slate-200 bg-white py-16">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">From walkthrough to bank in 5 steps</h2>
          <div className="mt-8 grid gap-4 md:grid-cols-5">
            {["Select property & unit", "Capture video, photos & voice notes", "Tag findings: category, severity, cost", "Generate AI draft summary", "Export PDF / CSV & email"].map((s, i) => (
              <div key={i} className="rounded-xl border border-slate-200 p-5">
                <div className="font-display text-3xl font-extrabold text-blue-600">{i + 1}</div>
                <p className="mt-2 text-sm font-medium text-slate-700">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-6xl px-4 py-16">
        <div className="text-center">
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight sm:text-4xl">Simple subscription pricing</h2>
          <p className="mt-3 text-slate-600">Extra view-only stakeholders can be added as seats on any plan.</p>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {plans.map((p, i) => (
            <div key={p.lookup_key} data-testid={`plan-card-${p.lookup_key}`}
              className={`relative rounded-2xl border p-7 ${i === 1 ? "border-blue-600 bg-slate-900 text-white shadow-xl" : "border-slate-200 bg-white"}`}>
              {i === 1 && <span className="absolute -top-3 left-7 rounded-full bg-blue-600 px-3 py-1 font-mono text-xs uppercase tracking-widest text-white">Most popular</span>}
              <div className="font-display text-xl font-bold uppercase">{p.name}</div>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-4xl font-extrabold">${p.price}</span>
                <span className={`mb-1 text-sm ${i === 1 ? "text-slate-300" : "text-slate-500"}`}>/mo</span>
              </div>
              <ul className="mt-6 space-y-2.5 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className={`mt-0.5 h-4 w-4 shrink-0 ${i === 1 ? "text-blue-400" : "text-blue-600"}`} />
                    <span className={i === 1 ? "text-slate-200" : "text-slate-700"}>{f}</span>
                  </li>
                ))}
              </ul>
              <button data-testid={`plan-select-${p.lookup_key}`} onClick={() => nav("/register")}
                className={`mt-7 w-full rounded-xl px-4 py-3 text-sm font-semibold ${i === 1 ? "bg-blue-600 text-white hover:bg-blue-500" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                Start free trial
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-4 pb-20">
        <div className="flex flex-col items-center gap-4 rounded-3xl bg-slate-900 px-6 py-14 text-center text-white">
          <ShieldCheck className="h-10 w-10 text-blue-400" />
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight">Ready to inspect faster?</h2>
          <p className="max-w-xl text-slate-300">Create your organization account, invite your inspectors, and generate your first bank-ready report today.</p>
          <Link to="/register" data-testid="cta-start-btn" className="mt-2 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-8 py-3.5 font-semibold hover:bg-blue-700">
            <Building2 className="h-4 w-4" /> Create free account
          </Link>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-2 px-4 text-center text-sm text-slate-500">
          <Logo className="h-16" />
          <p>© {new Date().getFullYear()} PropertyInspect360 · Inspect • Document • Manage • Anywhere</p>
          <Link to="/login" data-testid="footer-admin-link" className="mt-1 inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-400 transition-colors hover:border-blue-300 hover:text-blue-600">
            <Lock className="h-3 w-3" /> Admin Access
          </Link>
        </div>
      </footer>
    </div>
  );
}
